import axios from '../axios'
import { COURSEAIROUTES } from 'src/router/Api'

export type CourseAiSkill =
	| 'writing'
	| 'dictation'
	| 'pronunciation'
	| 'speaking'
	| 'combo'

export type CourseAiTopicVisibility = 'public' | 'invisible'

export interface CourseAiCourse {
	id: string
	name?: string | null
	title?: string | null
	type?: string | null
	skill?: string | null
	slug?: string | null
	[key: string]: any
}

export interface CourseAiTopicStat {
	total_lession?: number
	public?: number
	invisible?: number
	leaners_count?: number
	last_updated?: string | null
	[key: string]: any
}

export interface CourseAiTopic {
	id: string
	course_id?: string
	title?: string | null
	level?: string | null
	topic?: string | null
	original_text?: string | null
	/** Speaking lessons (UD-671): the intro and the 5 questions with their optional hints. */
	intro?: string | null
	questions?: { text: string; hint?: string | null }[] | null
	audio_url?: string | null
	day?: number | null
	day_index?: number | null
	order?: number | null
	visibility?: CourseAiTopicVisibility | string | null
	is_active?: boolean | null
	is_public?: boolean | null
	learners_count?: number | null
	leaners_count?: number | null
	updated_at?: string | null
	created_at?: string | null
	deleted_at?: string | null
	/**
	 * Listening topics only (UD-545): why the server skips this topic when it picks the topic
	 * of a day, for example "too long (332 words)". Null when it is not skipped, missing from
	 * an older server.
	 */
	skip_reason?: string | null
	[key: string]: any
}

export interface WritingTopicPayload {
	course_id: string
	title: string
	level: string
	topic: string
	visibility?: string
	is_active?: boolean
}

export interface DictationTopicPayload {
	course_id: string
	title: string
	level: string
	topic: string
	audio_url?: string
	original_text?: string
	visibility?: string
	is_active?: boolean
}

export interface WritingTopicUpdatePayload {
	title?: string
	level?: string
	topic?: string
	visibility?: string
	is_active?: boolean
}

export interface DictationTopicUpdatePayload {
	title?: string
	level?: string
	topic?: string
	audio_url?: string
	original_text?: string
	visibility?: string
	is_active?: boolean
}

const asArray = (value: any): any[] => {
	if (Array.isArray(value)) return value
	if (Array.isArray(value?.rows)) return value.rows
	if (Array.isArray(value?.data)) return value.data
	if (Array.isArray(value?.items)) return value.items
	return []
}

export const unwrapList = (response: any): { rows: any[]; count: number } => {
	const results = response?.data?.results
	let rows: any[] = []
	if (Array.isArray(results?.objects?.rows)) rows = results.objects.rows
	else if (Array.isArray(results?.object?.rows)) rows = results.object.rows
	else if (Array.isArray(results?.objects)) rows = results.objects
	else if (Array.isArray(results?.object)) rows = results.object
	else if (Array.isArray(results)) rows = results
	else rows = asArray(results?.objects ?? results?.object)

	const count =
		results?.objects?.count ?? results?.object?.count ?? rows.length
	return { rows, count: Number(count) || 0 }
}

export const unwrapObject = <T = any>(response: any): T | null => {
	const results = response?.data?.results
	return (results?.object ?? results?.objects ?? results ?? null) as T | null
}

export const getCourseAiList = async (params?: Record<string, any>) =>
	axios.get(COURSEAIROUTES.list, { params })

export const getCourseAiDetail = async (id: string) =>
	axios.get(COURSEAIROUTES.detail(id))

export const getCourseAiTopicStat = async (courseId: string) =>
	axios.get(COURSEAIROUTES.topicStat, { params: { course_id: courseId } })

export const getWritingTopics = async (params?: { course_id?: string }) =>
	axios.get(COURSEAIROUTES.writingTopics, {
		params: {
			...params,
			where: JSON.stringify({ deleted_at: { $is: null } }),
		},
	})

export const createWritingTopic = async (body: WritingTopicPayload) =>
	axios.post(COURSEAIROUTES.writingTopics, body)

export const updateWritingTopic = async (
	id: string,
	body: WritingTopicUpdatePayload,
) => axios.put(COURSEAIROUTES.writingTopic(id), body)

export const deleteWritingTopic = async (id: string) =>
	axios.delete(COURSEAIROUTES.writingTopic(id))

/** List CMS lesson prototypes (not daily instance topics). */
export const getDictationTopics = async (params?: {
	course_id?: string
	limit?: number
}) =>
	axios.get(COURSEAIROUTES.dictationTopicPrototypes, {
		params: {
			...params,
			where: JSON.stringify({ deleted_at: { $is: null } }),
		},
	})

export const createDictationTopic = async (body: DictationTopicPayload) =>
	axios.post(COURSEAIROUTES.dictationTopicPrototypes, body)

export const updateDictationTopic = async (
	id: string,
	body: DictationTopicUpdatePayload,
) => axios.put(COURSEAIROUTES.dictationTopic(id), body)

export const deleteDictationTopic = async (id: string) =>
	axios.delete(COURSEAIROUTES.dictationTopic(id))

// --- UD-393: Manage Learners (SUPERADMIN) ---

export type LearnerPaymentType = 'purchase' | 'repurchase' | 'resume'
export type LearnerTrialState = 'on_trial' | 'trial_ended' | 'paid_after_trial'

export interface CourseAiLearnerPayment {
	/** The payment row; a staff-created refund names it. */
	payment_id?: string | null
	paid_at: string
	amount: number
	method: string | null
	method_label: string
	type: LearnerPaymentType
	transaction_ref: string
}

/** A refund staff recorded, shown as its own line of the payment history. */
export interface CourseAiLearnerRefundLine {
	id: string
	refunded_at: string
	amount: number
	amount_paid: number
	payment_id?: string | null
	transaction_ref: string
	note: string | null
	source: CourseAiRefundSource
}

export interface CourseAiLearnerRow {
	no: number
	id: string
	user_id: string
	name?: string | null
	phone?: string | null
	nationality?: string | null
	location?: string | null
	course_id: string
	course_name?: string | null
	level: string
	payment_status: 'purchased' | 'trial' | 'unpaid' | string
	course_status: 'on_going' | 'inactive' | 'cancel' | 'complete' | string
	latest_payment: CourseAiLearnerPayment | null
	payments_count: number
	payments: CourseAiLearnerPayment[]
	complete_days: number | null
	complete_days_max: number
	days_off: number
	days_off_limit: number
	free_trial: { state: LearnerTrialState; started_at: string | null; ends_at: string | null } | null
	enrolled_at?: string
	/** Set on an account that joined with a staff code; such rows are never counted. */
	staff_code?: 'TUTOR' | 'PURCHASED' | string | null
	/**
	 * UD-627: set on the row Kick is offered on, the staff code join itself while it gives
	 * the course: a tutor row, or the course a UniVini Tester code was entered on. Never on
	 * a paid row, a trial or a row another course opened. A server without Kick sends none.
	 */
	code_entry?: 'TUTOR' | 'PURCHASED' | string | null
	/** The learner's newest submission in this course. */
	last_active_at?: string | null
	/** Locked out of the course by a recorded refund, until they buy again. */
	refunded?: boolean
	refunds?: CourseAiLearnerRefundLine[]
}

export interface CourseAiSupportPerson {
	id: string
	name: string | null
	avatar: string | null
}

export interface CourseAiSupportMessage {
	id: string
	type: string
	content: string | null
	created_at: string
	edited_at: string | null
	from: 'support' | 'learner'
	sender: CourseAiSupportPerson | null
	medias?: { url: string; type: string }[]
}

export interface CourseAiSupportConversation {
	id: string
	created_at: string | null
	last_time_chat: string | null
	learner: CourseAiSupportPerson & { phone: string | null }
	support: CourseAiSupportPerson
}

export interface CourseAiLearnerShare {
	count: number
	percent: number
}

export interface CourseAiLearnersSummary {
	total_learners: number
	ongoing: CourseAiLearnerShare
	inactive: CourseAiLearnerShare
	cancelled: CourseAiLearnerShare
	completed: CourseAiLearnerShare
	free_trial: { total: number; on_trial: number; trial_ended: number; paid_after_trial: number }
	abandoned: number
	refunded?: number
	revenue: {
		/** Gross: every successful payment. */
		total: number
		transactions: number
		resume_total: number
		resume_transactions: number
		refund_total?: number
		refund_count?: number
		net_total?: number
		from: string | null
		to: string | null
		currency: string
	}
}

export interface CourseAiLearnerFilterOptions {
	/** `type` (UD-671) lets Manage Learners offer Speaking sessions on the rows of a SPEAKING course; an older server sends none. */
	courses: { id: string; name: string; type?: string | null }[]
	levels: string[]
	off_days: string[]
	course_statuses: string[]
	payment_statuses: string[]
	payment_methods: { value: string; label: string }[]
	learner_types?: string[]
	staff_codes?: string[]
}

export interface CourseAiLearnerQuery {
	search?: string
	course_id?: string
	level?: string
	off_days?: string
	course_status?: string
	payment_method?: string
	payment_status?: string
	learner_type?: string
	from?: string
	to?: string
	page?: number
	limit?: number
}

const cleanParams = (params: Record<string, any>) =>
	Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== '',
		),
	)

export const getCourseAiLearners = async (params: CourseAiLearnerQuery) =>
	axios.get(COURSEAIROUTES.learners, { params: cleanParams(params) })

export const getCourseAiLearnersSummary = async (params: CourseAiLearnerQuery) =>
	axios.get(COURSEAIROUTES.learnersSummary, { params: cleanParams(params) })

export const getCourseAiLearnersRevenueByMonth = async (params: CourseAiLearnerQuery) =>
	axios.get(COURSEAIROUTES.learnersRevenueByMonth, { params: cleanParams(params) })

/** revenue is gross; a refund counts on the day it was recorded (Vietnam time). */
export interface CourseAiRevenueFigures {
	revenue: number
	transactions: number
	resume_revenue: number
	resume_transactions: number
	refunds?: number
	refund_count?: number
	net_revenue?: number
}

export interface CourseAiRevenueMonthRow extends CourseAiRevenueFigures {
	month: string
}

export interface CourseAiRevenueDayRow extends CourseAiRevenueFigures {
	day: string
}

export interface CourseAiRevenueByDay {
	month: string
	rows: CourseAiRevenueDayRow[]
	totals: CourseAiRevenueFigures
	currency: string
	timezone?: string
}

/** The days of one month (YYYY-MM) that had a payment or a refund, oldest first. */
export const getCourseAiLearnersRevenueByDay = async (
	params: CourseAiLearnerQuery & { month: string },
) => axios.get(COURSEAIROUTES.learnersRevenueByDay, { params: cleanParams(params) })

export const exportCourseAiLearners = async (params: CourseAiLearnerQuery) =>
	axios.get(COURSEAIROUTES.learnersExport, { params: cleanParams(params) })

// --- UD-393: refunds (SUPERADMIN). Staff record them; the money is sent back outside the CMS. ---

export type CourseAiRefundStatus = 'pending' | 'refunded' | 'rejected'
export type CourseAiRefundSource = 'app_request' | 'staff_created'

export interface CourseAiRefund {
	no?: number
	id: string
	status: CourseAiRefundStatus | string
	source: CourseAiRefundSource | string
	user_id: string
	name: string | null
	phone: string | null
	course_id: string
	course_name: string | null
	payment_id: string
	transaction_ref: string
	amount_paid: number
	refunded_amount: number | null
	/**
	 * On a Refunded row: whether the refund locked the learner out of the course.
	 * Null on a pending or rejected request; a server without the choice sends none.
	 */
	locks_course?: boolean | null
	type_of_issue: string | null
	reason: string | null
	requested_at: string
	note: string | null
	handled_at: string | null
	handled_by: { id: string; name: string | null } | null
}

/** What the server did after recording a refund: the class chat and the counts. */
export interface CourseAiRefundEffects {
	/** False for a refund that kept the learner in the course: no lockout ran, none can be run again. */
	lockout_applied?: boolean
	class_chat: 'removed' | 'not_locked' | 'no_class_chat' | 'not_in_chat' | 'not_a_plain_member' | 'failed' | string
	caches_cleared: boolean
}

export interface CourseAiRefundResult {
	refund: CourseAiRefund | null
	effects?: CourseAiRefundEffects
}

export const getCourseAiRefunds = async (params: {
	status?: string
	page?: number
	limit?: number
}) => axios.get(COURSEAIROUTES.learnerRefunds, { params: cleanParams(params) })

/**
 * Records that the money was sent back. The learner is then locked out of the
 * course, unless locks_course is false: that refund only comes off the revenue.
 * A body without locks_course locks the course.
 */
export const markCourseAiRefundRefunded = async (
	id: string,
	body: { refunded_amount: number; note?: string; locks_course?: boolean },
) => axios.post(COURSEAIROUTES.learnerRefundRefunded(id), body)

/** Runs the lockout of an already Refunded request again; it changes no refund. */
export const reapplyCourseAiRefundLockout = async (id: string) =>
	axios.post(COURSEAIROUTES.learnerRefundLockout(id))

export const rejectCourseAiRefund = async (id: string, body: { note: string }) =>
	axios.post(COURSEAIROUTES.learnerRefundReject(id), body)

/** A refund for a learner who never sent a request in the app; saved as Refunded straight away. */
export const createCourseAiRefund = async (body: {
	user_id: string
	course_id: string
	payment_id?: string
	refunded_amount: number
	note: string
	locks_course?: boolean
}) => axios.post(COURSEAIROUTES.learnerRefunds, body)

export const getCourseAiLearnerFilterOptions = async () =>
	axios.get(COURSEAIROUTES.learnersFilters)

/** Opens (or reuses) the learner's Course AI support chat; the server acts as the support account. */
export const openLearnerSupportConversation = async (userId: string) =>
	axios.post(COURSEAIROUTES.learnerSupportConversation(userId))

/** Newest first. */
export const getLearnerSupportMessages = async (
	userId: string,
	params?: { page?: number; limit?: number },
) => axios.get(COURSEAIROUTES.learnerSupportMessages(userId), { params })

export const sendLearnerSupportMessage = async (userId: string, content: string) =>
	axios.post(COURSEAIROUTES.learnerSupportMessages(userId), { content })

// --- UD-627: the two staff codes and Kick (SUPERADMIN) ---

/** tutor is the Tutor code, purchased the UniVini Tester code. */
export type StaffJoinCodeKind = 'tutor' | 'purchased'

export interface CourseAiStaffJoinCodes {
	tutor_code: string
	purchased_code: string
	/** After a change: the code that was changed. */
	changed?: StaffJoinCodeKind
}

export const getCourseAiStaffJoinCodes = async () => axios.get(COURSEAIROUTES.staffJoinCodes)

/** Replaces that one code. The other code stays as it is, and both come back in the answer. */
export const regenerateCourseAiStaffJoinCode = async (kind: StaffJoinCodeKind) =>
	axios.post(COURSEAIROUTES.staffJoinCodeRegenerate(kind))

export interface CourseAiKickedCourse {
	course_id: string
	course_name: string | null
	effects: CourseAiRefundEffects
}

export interface CourseAiKickResult {
	user_id: string
	code_entry: 'TUTOR' | 'PURCHASED' | null
	/** The row was out already: only the steps after the lock (class chat, counts) were run again. */
	already_removed: boolean
	/** Every course the person lost: the course of the row and the others the same code had opened. */
	courses: CourseAiKickedCourse[]
	/** Tutor rows that could not be done this time; each still shows its Kick. */
	not_kicked_course_ids?: string[]
}

/**
 * Kicks the person of that Manage Learners row out of the course, and out of the other
 * courses the same staff code opened. The server refuses a row that is not a staff code join.
 */
export const kickCourseAiStaffLearner = async (userCourseId: string) =>
	axios.post(COURSEAIROUTES.learnerKick(userCourseId))

// --- UD-671: Speaking (lessons, topics of a date, talks of a learner) ---

export interface SpeakingQuestion {
	text: string
	hint?: string | null
}

/** A Basic UniVini lesson of the Speaking library: title, level, intro and exactly 5 questions. */
export interface SpeakingLessonPayload {
	course_id?: string
	title: string
	level: string | null
	intro: string | null
	questions: SpeakingQuestion[]
	is_active?: boolean
}

/** List the Speaking lessons of a course (the library the Basic UniVini set rotates through, by day index). */
export const getSpeakingTopicPrototypes = async (params?: {
	course_id?: string
	limit?: number
}) => axios.get(COURSEAIROUTES.speakingTopicPrototypes, { params })

export const createSpeakingTopicPrototype = async (body: SpeakingLessonPayload) =>
	axios.post(COURSEAIROUTES.speakingTopicPrototypes, body)

export const updateSpeakingTopicPrototype = async (
	id: string,
	body: Partial<SpeakingLessonPayload>,
) => axios.put(COURSEAIROUTES.speakingTopicPrototype(id), body)

export const deleteSpeakingTopicPrototype = async (id: string) =>
	axios.delete(COURSEAIROUTES.speakingTopicPrototype(id))

export type SpeakingTopicCategory = 'MANUAL' | 'HOT_TOPICS' | 'IELTS_TOPICS'

export interface SpeakingIeltsPart2 {
	cue_card: string
	bullets: string[]
	explain: string
	prep_seconds?: number
	talk_seconds?: number
}

export interface SpeakingIeltsData {
	part1_theme: string
	part2: SpeakingIeltsPart2
	rounding_off: string[]
}

export interface SpeakingGroundingSource {
	title?: string | null
	uri?: string | null
}

/** One set offered on a date. `started` is how many learners started a talk on it. */
export interface SpeakingDailyTopic {
	id: string
	course_id: string
	date: string
	category: SpeakingTopicCategory
	set_position: number
	title: string
	level: string | null
	intro: string | null
	questions: SpeakingQuestion[]
	ielts: SpeakingIeltsData | null
	minutes: number
	sources: SpeakingGroundingSource[]
	prototype_id: string | null
	prototype_day_index: number | null
	started: number
	can_edit: boolean
	can_regenerate: boolean
}

export interface SpeakingTopicsOfDate {
	course_id: string
	date: string
	today: string
	is_past: boolean
	topics: SpeakingDailyTopic[]
}

export interface SpeakingTopicEditPayload {
	title?: string
	level?: string | null
	intro?: string | null
	questions?: SpeakingQuestion[]
	ielts?: {
		part1_theme?: string
		part2?: { cue_card?: string; bullets?: string[]; explain?: string }
		rounding_off?: string[]
	} | null
}

export const getSpeakingTopicsOfDate = async (courseId: string, date: string) =>
	axios.get(COURSEAIROUTES.speakingTopics, { params: { course_id: courseId, date } })

/** Text edit of one topic. The server answers 409 once a learner started a talk on it. */
export const updateSpeakingTopic = async (id: string, body: SpeakingTopicEditPayload) =>
	axios.put(COURSEAIROUTES.speakingTopic(id), body)

/** Regenerate the Hot and IELTS sets of a date (or one of them). Staff only; Gemini, so it takes a while. */
export const regenerateSpeakingTopics = async (body: {
	course_id: string
	date: string
	category?: 'HOT_TOPICS' | 'IELTS_TOPICS'
}) => axios.post(COURSEAIROUTES.speakingTopicsGenerate, body)

export interface SpeakingSessionCriteria {
	fluency?: number | null
	vocabulary?: number | null
	grammar?: number | null
	pronunciation?: number | null
	[key: string]: number | null | undefined
}

export interface SpeakingSessionRow {
	id: string
	user_id: string
	user_name: string | null
	user_phone: string | null
	course_id: string
	submission_id: string
	topic: { id: string; date: string | null; category: SpeakingTopicCategory | null; title: string | null }
	mode: 'standard' | 'ielts' | string
	status: 'active' | 'ending' | 'submitted' | string
	end_reason: string | null
	character_id: string
	level: string
	started_at: string
	ended_at: string | null
	limit_seconds: number
	learner_audio_ms: number
	vietnamese_turns: number
	silence_turns: number
	scoring_status: string | null
	score: number | null
	overall: number | null
	criteria: SpeakingSessionCriteria | null
	cost_usd: number
}

export interface SpeakingTranscriptTurn {
	id: string
	seq: number
	kind: string
	part: string | null
	question_index: number | null
	created_at: string | null
	learner: { transcript: string | null; language: string | null; audio_url: string | null; duration_ms: number | null }
	ai: { reply_text: string | null; audio_url: string | null; duration_ms: number | null }
	corrections: any[]
	moved_to_next: boolean
	pronunciation: { status: string | null; score: number | null }
	llm_ms: number | null
	tts_ms: number | null
	cost_usd: number
}

export interface SpeakingReviewMistake {
	id: string
	error_type: string
	word: string | null
	error: string | null
	correction: string | null
	explanation: string | null
	audio_url: string | null
	severity?: string | null
}

export interface SpeakingReviewQuestion {
	index: number
	question: string | null
	suggestion: string | null
	suggestion_audio_url: string | null
	mistakes: SpeakingReviewMistake[]
}

export interface SpeakingSessionDetail {
	session: Omit<SpeakingSessionRow, 'user_name' | 'user_phone' | 'topic' | 'score' | 'overall' | 'criteria' | 'scoring_status' | 'submission_id'> & {
		submission_id: string
		daily_topic_id: string
		voice_id: string
		deadline_at: string
		prep_seconds_used: number
		client_version: string | null
	}
	user: { id: string; name: string | null; phone: string | null }
	transcript: SpeakingTranscriptTurn[]
	review: {
		score: number | null
		overall: number | null
		criteria: SpeakingSessionCriteria | null
		ielts: any
		summary: any
		topic: { id: string; category: SpeakingTopicCategory | null; title: string | null } | null
		questions: SpeakingReviewQuestion[]
		other_mistakes: SpeakingReviewMistake[]
	}
}

/** The talks of a learner (SUPERADMIN only, it is learner data). */
export const getSpeakingSessions = async (params: {
	user_id?: string
	course_id?: string
	limit?: number
	offset?: number
}) => axios.get(COURSEAIROUTES.speakingSessions, { params: cleanParams(params) })

export const getSpeakingSession = async (id: string) =>
	axios.get(COURSEAIROUTES.speakingSession(id))
